import { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import SearchableDropdown from '../../../../components/SearchableDropdown';
import MemberRoleSelect from '../../../../components/MemberRoleSelect';
import { createOccupant, uploadOccupantDocument } from '../../../../redux/actions/SocietyPortal/OccupantAction/OccupantAction';
import { getWingsDropdown, getFlatsDropdown } from '../../../../redux/actions/WingsAndFlatsActions/WingsAndFlatsAction';
import { selectedSocietyStorer } from '../../../../utils/webStorer';
import usePermissions from '../../../../hook/usePermissions';
import { PERMISSIONS } from '../../../../consts/permissions';
import { SOCIETY_PORTAL, SOCIETY_PORTAL_MEMBERS_TENANTS } from '../../../../Routing/routes';

// Tenant document slots collected at onboarding. Same enum keys as the edit
// modal; files stay in local state until the tenant is created, then we
// POST them against the new occupant_id.
const PENDING_DOC_SLOTS = [
    { key: 'POLICE_NOC', label: 'Police NOC', icon: 'mdi:shield-check-outline' },
    { key: 'AGREEMENT',  label: 'Rental Agreement / Leave & License', icon: 'mdi:file-document-outline' }
];
const DOC_ACCEPT = '.pdf,.jpg,.jpeg,.png,.webp,.doc,.docx';

const AddTenant = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { createLoading } = useSelector(state => state.OccupantReducer);
    const { wingsDropdown, wingsDropdownLoading, flatsDropdown, flatsDropdownLoading } = useSelector(state => state.WingsAndFlatsReducers);
    const { can } = usePermissions();
    // Anyone who may add / edit members picks the login role; the options
    // (and the API) limit non-admins to member roles.
    const canPickRole = can(PERMISSIONS.USER_MANAGE_ROLES) || can(PERMISSIONS.OCCUPANT_CREATE);

    const wings = wingsDropdown || [];
    const flats = flatsDropdown || [];

    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        date_of_birth: '',
        wing_id: 'all',
        flat_id: 'all',
        is_resident: false,
        start_date: '',
        // Tenancy contract expiry — separate from actual move-out (end_date).
        agreement_end_date: '',
        role_id: '',
    });
    const [errors, setErrors] = useState({});
    // Files chosen before the tenant exists. Keyed by document_type so a user
    // can pick a Police NOC and an Agreement independently. Uploaded after
    // the create call succeeds and returns an occupant_id.
    const [pendingDocs, setPendingDocs] = useState({}); // { POLICE_NOC: File, AGREEMENT: File }

    useEffect(() => {
        const societyId = selectedSocietyStorer.getSocietyId();
        if (societyId) {
            dispatch(getWingsDropdown({ society_id: societyId }));
        }
    }, [dispatch]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleWingChange = (value) => {
        setFormData(prev => ({ ...prev, wing_id: value, flat_id: 'all' }));
        if (errors.wing_id) setErrors(prev => ({ ...prev, wing_id: '' }));
        if (value !== 'all') {
            dispatch(getFlatsDropdown({ wing_id: value }));
        }
    };

    // A tenant rents from the owner: a flat with no owner cannot take one.
    // `has_owner` comes with the flats dropdown; the API refuses it as well.
    const NO_OWNER_MSG = 'This flat has no owner. Add the owner before adding a tenant.';
    const flatHasNoOwner = (flatId) => {
        const flat = flats.find(f => String(f.flat_id) === String(flatId));
        return Boolean(flat) && flat.has_owner !== undefined && !Number(flat.has_owner);
    };

    const handleFlatChange = (value) => {
        setFormData(prev => ({ ...prev, flat_id: value }));
        setErrors(prev => ({ ...prev, flat_id: flatHasNoOwner(value) ? NO_OWNER_MSG : '' }));
    };

    const handleRoleChange = (value) => {
        setFormData(prev => ({ ...prev, role_id: value }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.first_name.trim()) newErrors.first_name = 'First Name is required';
        if (!formData.phone.trim()) {
            newErrors.phone = 'Phone is required';
        } else if (!/^\d{10}$/.test(formData.phone.trim())) {
            newErrors.phone = 'Phone must be exactly 10 digits';
        }
        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Invalid email format';
        }
        if (formData.wing_id === 'all') newErrors.wing_id = 'Wing is required';
        if (formData.flat_id === 'all') newErrors.flat_id = 'Flat is required';
        else if (flatHasNoOwner(formData.flat_id)) newErrors.flat_id = NO_OWNER_MSG;
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        const societyId = selectedSocietyStorer.getSocietyId();
        const payload = {
            society_id: societyId,
            first_name: formData.first_name,
            last_name: formData.last_name,
            email: formData.email || undefined,
            phone: formData.phone,
            is_resident: formData.is_resident,
            type: 'TENANT',
            wing_id: formData.wing_id,
            flat_id: formData.flat_id,
        };
        if (formData.date_of_birth) payload.date_of_birth = formData.date_of_birth;
        if (formData.start_date) payload.start_date = formData.start_date;
        if (formData.agreement_end_date) payload.agreement_end_date = formData.agreement_end_date;
        if (canPickRole && formData.role_id) payload.role_id = Number(formData.role_id);

        payload.onSuccess = (created) => {
            // If the user staged any documents, upload them against the fresh
            // occupant_id and then navigate. Failures surface as toasts from
            // the saga; we still navigate so the tenant isn't lost.
            const occupantId = created?.occupant_id;
            const stagedEntries = Object.entries(pendingDocs).filter(([, file]) => !!file);

            const goBack = () => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_MEMBERS_TENANTS);

            if (!occupantId || stagedEntries.length === 0) {
                goBack();
                return;
            }
            let remaining = stagedEntries.length;
            const done = () => { remaining -= 1; if (remaining <= 0) goBack(); };
            stagedEntries.forEach(([document_type, file]) => {
                dispatch(uploadOccupantDocument({
                    occupant_id: occupantId,
                    document_type,
                    file,
                    onSuccess: done,
                    onError: done
                }));
            });
        };

        dispatch(createOccupant(payload));
    };

    return (
        <>
            {/* Header */}
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
                <div className="tw-flex tw-items-center tw-gap-3">
                    <button
                        onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_MEMBERS_TENANTS)}
                        className="tw-p-2 tw-rounded-lg tw-bg-gray-100 hover:tw-bg-gray-200 tw-text-gray-600"
                    >
                        <Icon icon="mdi:arrow-left" className="tw-text-lg" />
                    </button>
                    <div>
                        <h1 className="tw-text-lg tw-font-semibold tw-text-gray-800">Add New Tenant</h1>
                        <p className="tw-text-xs tw-text-gray-500">Fill in the tenant details below</p>
                    </div>
                </div>
                <div className="tw-flex tw-items-center tw-gap-3">
                    <button
                        type="button"
                        onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_MEMBERS_TENANTS)}
                        className="tw-px-6 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-600 tw-bg-gray-100 hover:tw-bg-gray-200 tw-rounded-lg"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        form="addTenantForm"
                        disabled={createLoading}
                        className="tw-px-5 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-bg-blue-600 hover:tw-bg-blue-700 tw-rounded-lg tw-flex tw-items-center tw-gap-1.5 disabled:tw-opacity-50 tw-transition-colors"
                    >
                        {createLoading ? (
                            <>
                                <Icon icon="svg-spinners:ring-resize" className="tw-text-base" />
                                Saving...
                            </>
                        ) : (
                            <>
                                <Icon icon="mingcute:save-line" className="tw-text-base" />
                                Save
                            </>
                        )}
                    </button>
                </div>
            </div>

            <form id="addTenantForm" onSubmit={handleSubmit}>
                {/* Personal Information */}
                <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-p-4 tw-mb-4">
                    <h2 className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-mb-3 tw-flex tw-items-center tw-gap-2">
                        <Icon icon="mdi:account-outline" className="tw-text-blue-500" />
                        Personal Information
                    </h2>
                    <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-4 tw-gap-4">
                        <div>
                            <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">
                                First Name <span className="tw-text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="first_name"
                                value={formData.first_name}
                                onChange={handleChange}
                                className={`tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 ${errors.first_name ? 'tw-border-red-400' : 'tw-border-gray-200'}`}
                                placeholder="Enter first name"
                                maxLength={50}
                            />
                            {errors.first_name && <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.first_name}</p>}
                        </div>
                        <div>
                            <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">
                                Last Name
                            </label>
                            <input
                                type="text"
                                name="last_name"
                                value={formData.last_name}
                                onChange={handleChange}
                                className={`tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 ${errors.last_name ? 'tw-border-red-400' : 'tw-border-gray-200'}`}
                                placeholder="Enter last name"
                                maxLength={50}
                            />
                            {errors.last_name && <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.last_name}</p>}
                        </div>
                        <div>
                            <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">
                                Phone <span className="tw-text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                className={`tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 ${errors.phone ? 'tw-border-red-400' : 'tw-border-gray-200'}`}
                                placeholder="Phone Number"
                                maxLength={15}
                            />
                            {errors.phone && <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.phone}</p>}
                        </div>
                        <div>
                            <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Email</label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                className={`tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 ${errors.email ? 'tw-border-red-400' : 'tw-border-gray-200'}`}
                                placeholder="Email Address"
                                maxLength={100}
                            />
                            {errors.email && <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.email}</p>}
                        </div>
                        <div>
                            <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Date of Birth</label>
                            <input
                                type="date"
                                name="date_of_birth"
                                value={formData.date_of_birth}
                                onChange={handleChange}
                                className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500"
                            />
                        </div>
                    </div>
                </div>

                {/* Unit Details */}
                <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-p-4 tw-mb-4">
                    <h2 className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-mb-3 tw-flex tw-items-center tw-gap-2">
                        <Icon icon="mdi:home-outline" className="tw-text-blue-500" />
                        Unit Details
                    </h2>
                    <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-4 tw-gap-4">
                        <div>
                            <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">
                                Wing <span className="tw-text-red-500">*</span>
                            </label>
                            <SearchableDropdown
                                value={formData.wing_id}
                                onChange={handleWingChange}
                                options={wings}
                                valueKey="wing_id"
                                labelKey="title"
                                placeholder="Select Wing"
                                loading={wingsDropdownLoading}
                                className="tw-w-full"
                            />
                            {errors.wing_id && <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.wing_id}</p>}
                        </div>
                        <div>
                            <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">
                                Flat <span className="tw-text-red-500">*</span>
                            </label>
                            <SearchableDropdown
                                value={formData.flat_id}
                                onChange={handleFlatChange}
                                options={flats}
                                valueKey="flat_id"
                                labelKey="flat_number"
                                placeholder="Select Flat"
                                loading={flatsDropdownLoading}
                                className="tw-w-full"
                            />
                            {errors.flat_id && <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.flat_id}</p>}
                        </div>
                        <div>
                            <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Start Date</label>
                            <input
                                type="date"
                                name="start_date"
                                value={formData.start_date}
                                onChange={handleChange}
                                className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500"
                            />
                        </div>
                        <div>
                            <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Agreement End Date</label>
                            <input
                                type="date"
                                name="agreement_end_date"
                                value={formData.agreement_end_date}
                                min={formData.start_date || undefined}
                                onChange={handleChange}
                                className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500"
                            />
                        </div>
                        <div className="tw-flex tw-items-center tw-gap-3 tw-bg-gray-50 tw-rounded-lg tw-px-3 tw-py-2.5 tw-border tw-border-gray-100">
                            <label className="tw-relative tw-inline-flex tw-items-center tw-cursor-pointer">
                                <input
                                    type="checkbox"
                                    name="is_resident"
                                    className="tw-sr-only tw-peer"
                                    checked={formData.is_resident}
                                    onChange={handleChange}
                                />
                                <div className="tw-w-9 tw-h-5 tw-bg-gray-300 tw-rounded-full peer-checked:tw-bg-emerald-500 tw-transition-colors after:tw-content-[''] after:tw-absolute after:tw-top-0.5 after:tw-left-0.5 after:tw-bg-white after:tw-rounded-full after:tw-h-4 after:tw-w-4 after:tw-transition-transform peer-checked:after:tw-translate-x-4"></div>
                            </label>
                            <span className={`tw-text-sm tw-font-medium ${formData.is_resident ? 'tw-text-emerald-600' : 'tw-text-gray-500'}`}>
                                {formData.is_resident ? 'Residing' : 'Non-Residing'}
                            </span>
                        </div>
                        {canPickRole && (
                            <div>
                                <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Login Role</label>
                                <MemberRoleSelect
                                    value={formData.role_id}
                                    onChange={handleRoleChange}
                                    placeholder="Default: Resident"
                                    className="tw-w-full"
                                />
                                <p className="tw-text-[10px] tw-text-gray-400 tw-mt-0.5">Leave blank to default to Resident</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Documents — staged locally; uploaded after the tenant is created */}
                <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-p-4 tw-mb-4">
                    <h2 className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-mb-1 tw-flex tw-items-center tw-gap-2">
                        <Icon icon="mdi:folder-multiple-outline" className="tw-text-blue-500" />
                        Documents <span className="tw-text-gray-400 tw-font-normal tw-text-xs">(Optional)</span>
                    </h2>
                    <p className="tw-text-[11px] tw-text-gray-500 tw-mb-3">
                        Files upload after the tenant is saved. PDF, image, or Word up to 10 MB.
                    </p>
                    <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-3">
                        {PENDING_DOC_SLOTS.map((slot) => {
                            const staged = pendingDocs[slot.key];
                            return (
                                <div
                                    key={slot.key}
                                    className="tw-border tw-border-gray-200 tw-rounded-lg tw-p-2.5 tw-flex tw-items-center tw-gap-3"
                                >
                                    <Icon icon={slot.icon} className="tw-text-blue-500 tw-text-base tw-flex-shrink-0" />
                                    <div className="tw-flex-1 tw-min-w-0">
                                        <div className="tw-text-xs tw-font-medium tw-text-gray-700">{slot.label}</div>
                                        {staged ? (
                                            <p className="tw-text-[11px] tw-text-gray-600 tw-truncate" title={staged.name}>
                                                <Icon icon="mdi:paperclip" className="tw-inline tw-text-xs tw-mr-0.5" />
                                                {staged.name}
                                            </p>
                                        ) : (
                                            <p className="tw-text-[11px] tw-text-gray-400">No file selected</p>
                                        )}
                                    </div>
                                    {staged && (
                                        <button
                                            type="button"
                                            onClick={() => setPendingDocs(prev => {
                                                const { [slot.key]: _drop, ...rest } = prev;
                                                return rest;
                                            })}
                                            className="tw-text-red-500 hover:tw-text-red-700 tw-p-1"
                                            title="Remove"
                                        >
                                            <Icon icon="mdi:trash-can-outline" className="tw-text-sm" />
                                        </button>
                                    )}
                                    <label className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2.5 tw-py-1 tw-text-[11px] tw-rounded-md tw-border tw-border-blue-200 tw-text-blue-700 hover:tw-bg-blue-50 tw-cursor-pointer">
                                        <Icon icon="mdi:upload-outline" className="tw-text-sm" />
                                        {staged ? 'Replace' : 'Choose'}
                                        <input
                                            type="file"
                                            accept={DOC_ACCEPT}
                                            onChange={(e) => {
                                                const file = e.target.files?.[0];
                                                if (file) setPendingDocs(prev => ({ ...prev, [slot.key]: file }));
                                                e.target.value = '';
                                            }}
                                            className="tw-hidden"
                                        />
                                    </label>
                                </div>
                            );
                        })}
                    </div>
                </div>

            </form>
        </>
    );
};

export default AddTenant;
